/**
 * Shared utilities for Ada business form data extraction and saving.
 * Used by both AdaReviewBusinessInfo (registration flow) and AdaReviewModal (post-registration).
 */

import restClient from 'restClient'
import type {
  BusinessRegNumber,
  ExtractedFormValues,
  SocialLink,
} from 'types/orgs'
import { AdaRegistrationResponse, adaRegistrationService } from '../services/adaRegistrationService'
import { fetchImageAsBase64WithDetails } from './logoUtils'

const API_URL = window.API_ROOT + '/api'
const restClientInstance = restClient(API_URL)

const deepMerge = (target: Record<string, any>, source: Record<string, any>): Record<string, any> => {
  const result = { ...target }

  for (const key of Object.keys(source)) {
    const sourceValue = source[key]
    const targetValue = target[key]

    if (
      sourceValue !== null &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue !== null &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      result[key] = deepMerge(targetValue, sourceValue)
    } else {
      result[key] = sourceValue
    }
  }

  return result
}

export const extractFormValuesFromAdaResponse = (response: AdaRegistrationResponse): ExtractedFormValues => {
  const values: ExtractedFormValues = {}
  const socialLinks: SocialLink[] = []
  const reviews: { text: string }[] = []

  if (response.name) {
    values.company_name = response.name
  }
  if (response.company_email) {
    values.company_email = response.company_email
  }

  response.data?.forEach((step) => {
    const rawData = step.data
    const stepData = Array.isArray(rawData) ? rawData[0] : rawData
    if (!stepData) return

    switch (step.key) {
      case 'business_details':
        if (stepData.phone_number) {
          values.sales_phone_number = stepData.phone_number
          if (stepData.phone_country_iso2 && stepData.phone_local) {
            values.sales_phone_number_metadata = {
              phone: stepData.phone_local,
              custom_data: {
                os_meta: {
                  phone_country_iso2: stepData.phone_country_iso2,
                },
              },
            }
          }
        }
        if (stepData.email) values.company_email = stepData.email
        if (stepData.logo_url) values.logo_url = stepData.logo_url
        if (stepData.color_scheme && stepData.color_scheme.length > 0) {
          values.color_highlight = stepData.color_scheme[0]
        }
        if (Array.isArray(stepData.registration_numbers) && stepData.registration_numbers.length > 0) {
          values.business_reg_numbers = stepData.registration_numbers as BusinessRegNumber[]
        }
        break

      case 'location_details':
        if (stepData.postcode) values.zip = stepData.postcode
        if (stepData.state) values.state = stepData.state
        if (stepData.city) values.locality = stepData.city
        if (stepData.address) values.address = stepData.address
        break

      case 'about_us':
        if (stepData.about_us_content) values.about_content = stepData.about_us_content
        break

      case 'why_choose_us':
        if (stepData.why_choose_us_content) values.why_choose_us = stepData.why_choose_us_content
        break

      case 'social_media':
        if (stepData.linkedin_url) socialLinks.push({ url: stepData.linkedin_url, platform: 'linkedin' })
        if (stepData.facebook_url) socialLinks.push({ url: stepData.facebook_url, platform: 'facebook' })
        if (stepData.twitter_url) socialLinks.push({ url: stepData.twitter_url, platform: 'twitter' })
        if (stepData.instagram_url) socialLinks.push({ url: stepData.instagram_url, platform: 'instagram' })
        if (stepData.youtube_url) socialLinks.push({ url: stepData.youtube_url, platform: 'youtube' })
        if (stepData.tiktok_url) socialLinks.push({ url: stepData.tiktok_url, platform: 'tiktok' })
        if (stepData.pinterest_url) socialLinks.push({ url: stepData.pinterest_url, platform: 'pinterest' })
        if (stepData.yelp_url) socialLinks.push({ url: stepData.yelp_url, platform: 'yelp' })
        break

      case 'customer_reviews':
        if (Array.isArray(stepData.testimonials)) {
          stepData.testimonials.forEach((testimonial: any) => {
            const reviewText = testimonial.review_text || ''
            const customerName = testimonial.customer_name || ''
            const source = testimonial.source || ''
            const lines: string[] = []
            if (customerName) lines.push(`${customerName}:`)
            if (reviewText) lines.push(reviewText)
            if (source) lines.push(`Source: ${source}`)
            reviews.push({ text: lines.join('\n') })
          })
        }
        break

      case 'service_offering':
        if (stepData.service_offering && Array.isArray(stepData.service_offering)) {
          values.service_offering = stepData.service_offering
        }
        break
    }
  })

  if (socialLinks.length > 0) {
    values.social_links = socialLinks
  }
  if (reviews.length > 0) {
    values.customer_reviews = reviews
  }

  return values
}

export const convertSocialLinksToObject = (
  socialLinks: Array<SocialLink> | undefined
): Record<string, string> | null => {
  if (!socialLinks || socialLinks.length === 0) return null

  const socialLinksObj: Record<string, string> = {}
  socialLinks.forEach((link) => {
    if (link.url) {
      if (link.platform && link.platform !== 'other') {
        socialLinksObj[link.platform] = link.url
      } else {
        const url = link.url.toLowerCase()
        if (url.includes('linkedin')) socialLinksObj.linkedin = link.url
        else if (url.includes('facebook')) socialLinksObj.facebook = link.url
        else if (url.includes('twitter') || url.includes('x.com')) socialLinksObj.twitter = link.url
        else if (url.includes('instagram')) socialLinksObj.instagram = link.url
        else if (url.includes('youtube')) socialLinksObj.youtube = link.url
        else if (url.includes('tiktok')) socialLinksObj.tiktok = link.url
        else if (url.includes('pinterest')) socialLinksObj.pinterest = link.url
        else if (url.includes('yelp')) socialLinksObj.yelp = link.url
      }
    }
  })

  return Object.keys(socialLinksObj).length > 0 ? socialLinksObj : null
}

export interface SaveBusinessInfoOptions {
  orgId: number
  formValues: Record<string, any>
  logoUrl: string | null
  initialAiValues?: Record<string, any>
  includeOrgIdentityFields?: boolean
  existingOrgLogoUrl?: string | null
}

export const formatContentBlock1Html = (
  whyChooseUs: string | undefined,
  customerReviews: Array<{ text: string }> | undefined
): string | null => {
  const sections: string[] = []

  if (whyChooseUs && whyChooseUs.trim()) {
    sections.push(`
<div class="why-choose-us-section">
  <h2 style="font-size: 24px; font-weight: 600; margin-bottom: 16px; color: #333;">Why Choose Us</h2>
  <p style="font-size: 16px; line-height: 1.6; color: #555;">${whyChooseUs}</p>
</div>`)
  }

  if (customerReviews && customerReviews.length > 0) {
    const testimonialHtmlItems = customerReviews
      .map((review) => {
        const text = review.text || ''
        const lines = text.split('\n').filter((line) => line.trim())

        let customerInfo = ''
        let reviewText = ''
        let source = ''

        lines.forEach((line) => {
          if (line.startsWith('Source:')) {
            source = line.replace('Source:', '').trim()
          } else if (line.endsWith(':') && !customerInfo) {
            customerInfo = line.replace(':', '').trim()
          } else if (!reviewText) {
            reviewText = line.trim()
          }
        })

        if (!reviewText && lines.length > 0) {
          reviewText = text
        }

        return `
  <div style="margin-bottom: 24px; padding: 20px; background: #f9f9f9; border-radius: 8px; border-left: 4px solid #007bff;">
    <p style="font-size: 16px; font-style: italic; line-height: 1.6; color: #444; margin-bottom: 12px;">"${reviewText}"</p>
    ${
      customerInfo
        ? `<p style="font-size: 14px; font-weight: 600; color: #333; margin-bottom: 4px;">— ${customerInfo}</p>`
        : ''
    }
    ${source ? `<p style="font-size: 12px; color: #777;">Source: ${source}</p>` : ''}
  </div>`
      })
      .join('')

    sections.push(`
<div class="customer-testimonials-section" style="margin-top: 32px;">
  <h2 style="font-size: 24px; font-weight: 600; margin-bottom: 16px; color: #333;">What Our Customers Say</h2>
  ${testimonialHtmlItems}
</div>`)
  }

  if (sections.length === 0) return null

  return sections.join('\n')
}

export const updateProposalTemplateContentBlock1 = async (
  orgId: number,
  whyChooseUs: string | undefined,
  customerReviews: Array<{ text: string }> | undefined
): Promise<{ success: boolean; error?: string }> => {
  try {
    const contentHtml = formatContentBlock1Html(whyChooseUs, customerReviews)
    if (!contentHtml) {
      return { success: true }
    }

    const templatesResponse = await restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/proposal_templates/?is_default=true`,
    })
    const templatesData = templatesResponse.data
    const defaultTemplate = templatesData.results?.find((t: any) => t.is_default)

    if (!defaultTemplate) {
      const createResponse = await restClientInstance('CUSTOM_POST', 'custom', {
        url: `orgs/${orgId}/proposal_templates/`,
        data: {
          title: 'Default',
          is_default: true,
          type: 'opensolar',
        },
      })
      const createResult = createResponse.data

      let newSettings: Record<string, any> = {}
      if (createResult.settings) {
        try {
          newSettings =
            typeof createResult.settings === 'string' ? JSON.parse(createResult.settings) : createResult.settings
        } catch (e) {
          newSettings = {}
        }
      }

      const existingSections = newSettings.sections || ''
      const sectionsList = existingSections ? existingSections.split(',').map((s: string) => s.trim()) : []
      if (!sectionsList.includes('content_block_1')) {
        const financialBenefitsIndex = sectionsList.indexOf('financial_benefits')
        if (financialBenefitsIndex !== -1) {
          sectionsList.splice(financialBenefitsIndex + 1, 0, 'content_block_1')
        } else {
          sectionsList.push('content_block_1')
        }
        newSettings.sections = sectionsList.join(',')
      }

      newSettings.content_block_1 = contentHtml

      await restClientInstance('CUSTOM_PATCH', 'custom', {
        url: `orgs/${orgId}/proposal_templates/${createResult.id}/`,
        data: {
          settings: JSON.stringify(newSettings),
        },
      })

      return { success: true }
    }

    let settings: Record<string, any> = {}
    if (defaultTemplate.settings) {
      try {
        settings =
          typeof defaultTemplate.settings === 'string' ? JSON.parse(defaultTemplate.settings) : defaultTemplate.settings
      } catch (e) {
        settings = {}
      }
    }

    settings.content_block_1 = contentHtml

    const existingSections = settings.sections || ''
    const sectionsList = existingSections ? existingSections.split(',').map((s: string) => s.trim()) : []

    if (!sectionsList.includes('content_block_1')) {
      const financialBenefitsIndex = sectionsList.indexOf('financial_benefits')
      if (financialBenefitsIndex !== -1) {
        sectionsList.splice(financialBenefitsIndex + 1, 0, 'content_block_1')
      } else {
        sectionsList.push('content_block_1')
      }
      settings.sections = sectionsList.join(',')
    }

    await restClientInstance('CUSTOM_PATCH', 'custom', {
      url: `orgs/${orgId}/proposal_templates/${defaultTemplate.id}/`,
      data: { settings: JSON.stringify(settings) },
    })

    return { success: true }
  } catch (error: any) {
    return { success: false, error: error?.message || 'Failed to update proposal template' }
  }
}

export interface SaveBusinessInfoResult {
  success: boolean
  error?: string
  warning?: string
}

export const saveBusinessInfo = async (options: SaveBusinessInfoOptions): Promise<SaveBusinessInfoResult> => {
  const { orgId, formValues, logoUrl, initialAiValues, includeOrgIdentityFields, existingOrgLogoUrl } = options

  try {
    const orgUpdateData: Record<string, any> = {}

    if (includeOrgIdentityFields) {
      if (formValues.company_name) orgUpdateData.name = formValues.company_name
      if (formValues.company_email) orgUpdateData.company_email = formValues.company_email
    }

    if (formValues.sales_phone_number) orgUpdateData.sales_phone_number = formValues.sales_phone_number
    if (formValues.address) orgUpdateData.address = formValues.address
    if (formValues.locality) orgUpdateData.locality = formValues.locality
    if (formValues.state) orgUpdateData.state = formValues.state
    if (formValues.zip) orgUpdateData.zip = formValues.zip
    if (formValues.color_highlight) orgUpdateData.color_highlight = formValues.color_highlight
    if (formValues.about_content) orgUpdateData.about_content = formValues.about_content

    let hasLogoUpdate = false
    let logoUpdateData: Record<string, any> = {}
    let logoWarning: string | undefined

    const uploadedLogo = Array.isArray(formValues.logo) ? formValues.logo[0] : formValues.logo
    if (uploadedLogo && uploadedLogo.rawFile) {
      hasLogoUpdate = true
      const file = uploadedLogo.rawFile as File
      const base64Data = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.readAsDataURL(file)
      })
      logoUpdateData.logo_file_contents = {
        src: base64Data,
        title: file.name,
      }
    } else if (logoUrl && !formValues.logo) {
      const shouldUseAiLogo = !existingOrgLogoUrl
      if (shouldUseAiLogo) {
        const logoResult = await fetchImageAsBase64WithDetails(logoUrl)
        if (logoResult.success && logoResult.data) {
          hasLogoUpdate = true
          logoUpdateData.logo_file_contents = logoResult.data
        } else {
          logoWarning = logoResult.errorMessage || 'Failed to import logo from website'
        }
      }
    }

    const businessInfoData: Record<string, any> = {}

    if (formValues.why_choose_us) {
      businessInfoData.why_choose_us = formValues.why_choose_us
    }

    if ((formValues.business_reg_numbers?.length ?? 0) > 0) {
      orgUpdateData.business_reg_numbers = formValues.business_reg_numbers
    }

    const socialLinksObj = convertSocialLinksToObject(formValues.social_links)
    if (socialLinksObj) {
      businessInfoData.social_media_links = socialLinksObj
    }

    if (formValues.customer_reviews && formValues.customer_reviews.length > 0) {
      businessInfoData.customer_reviews = formValues.customer_reviews.map((review: { text: string }) => ({
        text: review.text,
      }))
    }

    if (Object.keys(orgUpdateData).length > 0) {
      await restClientInstance('UPDATE', 'orgs', {
        id: orgId,
        data: orgUpdateData,
      })
    }

    if (hasLogoUpdate) {
      await restClientInstance('UPDATE', 'content', {
        id: orgId,
        data: logoUpdateData,
      })
    }

    if (Object.keys(businessInfoData).length > 0) {
      let existingBusinessInfo: Record<string, any> = {}
      try {
        const getResponse = await restClientInstance('CUSTOM_GET', 'custom', {
          url: `orgs/${orgId}/configs/`,
        })
        existingBusinessInfo = getResponse.data?.org_configs?.business_info || {}
      } catch (e) {
        console.warn('[saveBusinessInfo] Failed to fetch existing configs:', e)
      }

      // Fetch existing business_info, deep merge with new data
      const mergedBusinessInfo = deepMerge(existingBusinessInfo, businessInfoData)

      await restClientInstance('CUSTOM_PUT', 'custom', {
        url: `orgs/${orgId}/configs/`,
        data: {
          business_info: mergedBusinessInfo,
        },
      })
    }

    if (formValues.why_choose_us || (formValues.customer_reviews && formValues.customer_reviews.length > 0)) {
      await updateProposalTemplateContentBlock1(orgId, formValues.why_choose_us, formValues.customer_reviews)
    }

    if (initialAiValues && Object.keys(initialAiValues).length > 0) {
      await adaRegistrationService.updateKpiLog(initialAiValues, formValues, orgId)
    }

    return { success: true, warning: logoWarning }
  } catch (error: any) {
    return {
      success: false,
      error: error?.message || 'Failed to save business information',
    }
  }
}
